/* ============================================================================
 * VENDOR.EXE — who the desk pays
 * ----------------------------------------------------------------------------
 * Ported from the design source (SupercomputeA/supercompute-immersion →
 * app-vendor.html): the vendor sheet, onboarding queue, invoices and rate card.
 *
 * WHAT IS REAL: the onboarding queue and the rate card are held in this browser.
 * WHAT IS NOT, AND SAYS SO: the vendor sheet and invoices are a specimen sheet.
 * A vendor reaches the sheet only after tax details are collected and a card
 * exists on the rail — nothing here charges a card on its own.
 * ========================================================================= */

import { useState } from 'react';
import { SkinArt } from '../components/SkinArt';

const TABS = [
  { id: 'vendors', label: 'Vendors' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'rates', label: 'Rate card' },
] as const;
type Tab = (typeof TABS)[number]['id'];

const VENDORS = [
  { name: 'Hostinger', sub: 'Two hosting subscriptions', category: 'web hosting', rail: 'AgentCard' },
];

const INVOICES = [
  { vendor: 'Hostinger', sub: 'hosting 1', amount: '$24.49' },
  { vendor: 'Hostinger', sub: 'hosting 2', amount: '$21.99' },
];

const CATEGORIES = ['web hosting', 'print and merch', 'freight', 'software', 'studio time'] as const;
const RAILS = ['AgentCard', 'stable transfer', 'invoice, manual'] as const;

const RATE_DEFS = [
  { key: 'consulting', label: 'Consulting hour', unit: '/ hour' },
  { key: 'event', label: 'Presence event, day rate', unit: '/ day' },
  { key: 'school', label: 'School seat', unit: '/ seat' },
  { key: 'merch', label: 'Merch margin', unit: '%' },
] as const;

interface OnboardEntry {
  name: string;
  cat: string;
  rail: string;
}

function readQueue(): OnboardEntry[] {
  try {
    return JSON.parse(localStorage.getItem('sc.vendor.onboarding') ?? '[]') as OnboardEntry[];
  } catch {
    return [];
  }
}
function readRates(): string[] {
  try {
    return JSON.parse(localStorage.getItem('sc.vendor.rates') ?? '[]') as string[];
  } catch {
    return [];
  }
}

export function Vendor() {
  const [tab, setTab] = useState<Tab>('vendors');
  const [queue, setQueue] = useState<OnboardEntry[]>(readQueue);
  const [name, setName] = useState('');
  const [cat, setCat] = useState<string>(CATEGORIES[0]);
  const [rail, setRail] = useState<string>(RAILS[0]);
  const [tax, setTax] = useState(false);
  const [rates, setRates] = useState<string[]>(() => {
    const saved = readRates();
    return RATE_DEFS.map((_, i) => saved[i] ?? '');
  });
  const [rateSaved, setRateSaved] = useState(false);

  const submitOnboard = () => {
    const n = name.trim();
    if (!n || !tax) return;
    const next = [{ name: n, cat, rail }, ...queue].slice(0, 10);
    setQueue(next);
    try {
      localStorage.setItem('sc.vendor.onboarding', JSON.stringify(next));
    } catch {
      /* non-fatal */
    }
    setName('');
    setTax(false);
  };

  const saveRates = () => {
    setRateSaved(true);
    try {
      localStorage.setItem('sc.vendor.rates', JSON.stringify(rates));
    } catch {
      /* non-fatal */
    }
  };

  const invTotal = INVOICES.reduce((a, i) => a + parseFloat(i.amount.replace('$', '')), 0).toFixed(2);

  return (
    <div className="dsp page">
      <header className="page__head">
        <SkinArt className="page__crest" src="/logos/dock-vendor.png" width={40} height={40} alt="" aria-hidden="true" />
        <div className="page__titles">
          <p className="eyebrow">vendor · onboarding · invoices · rate card</p>
          <h1 className="h1">Who the desk pays</h1>
        </div>
        <div className="page__chips">
          <span className="badge badge-live"><span className="lamp" aria-hidden="true" />{VENDORS.length} on record</span>
        </div>
      </header>

      <div className="tabs page__tabs" role="tablist" aria-label="Vendor modules">
        {TABS.map((t) => (
          <button key={t.id} role="tab" type="button" aria-selected={tab === t.id} tabIndex={tab === t.id ? 0 : -1} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ VENDORS ════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'vendors'}>
        <div className="grid3">
          <div className="stat-tile"><span className="label">vendors</span><span className="val">{VENDORS.length}</span><span className="sub">On the payables sheet.</span></div>
          <div className="stat-tile"><span className="label">subscriptions</span><span className="val">2</span><span className="sub">Both with the same host.</span></div>
          <div className="stat-tile"><span className="label">rail</span><span className="val" style={{ fontSize: 15 }}>AgentCard</span><span className="sub">A card is issued per charge.</span></div>
        </div>
        <div className="row row--between"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>the sheet</p><span className="mono">{VENDORS.length} rows</span></div>
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Vendor</th><th>Category</th><th>Rail</th><th>State</th></tr></thead>
            <tbody>
              {VENDORS.map((v, i) => (
                <tr key={i}><td>{v.name}<span className="sub">{v.sub}</span></td><td>{v.category}</td><td className="mono">{v.rail}</td><td><span className="badge badge-live"><span className="lamp" aria-hidden="true" />active</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="notice notice--brass"><span className="tick" aria-hidden="true" /><span><strong>One vendor only.</strong> The sheet says what it says. Onboard the next one below and it appears here.</span></p>
      </section>

      {/* ══ ONBOARDING ═════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'onboarding'}>
        <div className="grid2">
          <div className="stack">
            <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>onboard a vendor</p>
            <div>
              <label className="lbl micro" htmlFor="onName">Vendor</label>
              <input className="field" id="onName" type="text" placeholder="who is being paid" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid2">
              <div>
                <label className="lbl micro" htmlFor="onCat">Category</label>
                <select className="field" id="onCat" value={cat} onChange={(e) => setCat(e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="lbl micro" htmlFor="onRail">Pays by</label>
                <select className="field" id="onRail" value={rail} onChange={(e) => setRail(e.target.value)}>
                  {RAILS.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <label className="item" style={{ border: 0, padding: 0, gap: 9 }}>
              <input className="chk" type="checkbox" checked={tax} onChange={(e) => setTax(e.target.checked)} />
              <span>Tax details collected · <span className="mono">required before the first payout</span></span>
            </label>
            <button className="btn btn--gold" type="button" onClick={submitOnboard} disabled={name.trim().length < 2 || !tax}>Add to the sheet</button>
          </div>
          <div className="stack">
            <div className="row row--between"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>onboarding queue</p><span className="mono">{queue.length}</span></div>
            {queue.length === 0 ? (
              <p className="empty">Nobody waiting. The queue is clear.</p>
            ) : (
              <ul className="rows" style={{ border: 'var(--ui-bw) solid var(--ui-border)', borderRadius: 'var(--ui-radius-ctl)', background: 'var(--ui-canvas)', overflow: 'auto', maxHeight: 240 }}>
                {queue.map((v, i) => (
                  <li key={i} className="lrow"><span className="lrow__t">{v.name}</span><span className="tag tag--muted">{v.cat}</span><span className="mono">{v.rail}</span></li>
                ))}
              </ul>
            )}
            <p className="cap">A vendor reaches the sheet only after tax details are collected and the first card exists on the rail.</p>
          </div>
        </div>
      </section>

      {/* ══ INVOICES ═══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'invoices'}>
        <div className="row row--between">
          <div className="grow"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>recurring invoices</p><p className="cap">What lands in the agent mailbox, and what is already on autopilot.</p></div>
          <span className="tag tag--muted">as configured</span>
        </div>
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Vendor</th><th>Subscription</th><th className="r">Amount</th><th>Cycle</th><th>State</th></tr></thead>
            <tbody>
              {INVOICES.map((inv, i) => (
                <tr key={i}><td>{inv.vendor}</td><td className="mono">{inv.sub}</td><td className="r mono">{inv.amount}</td><td>monthly</td><td><span className="badge badge-live"><span className="lamp" aria-hidden="true" />auto</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid3">
          <div className="stat-tile"><span className="label">monthly</span><span className="val">${invTotal}</span><span className="sub">Summed from the rows above.</span></div>
          <div className="stat-tile"><span className="label">paper trail</span><span className="val" style={{ fontSize: 15 }}>mailbox</span><span className="sub">quantas.molt, on the bridge.</span></div>
          <div className="stat-tile"><span className="label">settlement</span><span className="val" style={{ fontSize: 15 }}>human</span><span className="sub">Signed off, then paid.</span></div>
        </div>
        <p className="cap">Dates are not recorded on this screen, because the desk reads them from the mailbox rather than guessing.</p>
      </section>

      {/* ══ RATE CARD ══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'rates'}>
        <div className="grid2">
          <div className="stack">
            <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>rate card</p>
            <p className="cap">What the fleet charges, set by a person. Left blank until someone fills it in.</p>
            <div className="stack">
              {RATE_DEFS.map((r, i) => (
                <div className="lrow" key={r.key}>
                  <span className="lrow__t">{r.label}</span>
                  <input className="field" type="text" placeholder="rate" aria-label={`${r.label} rate`} style={{ width: 132 }} value={rates[i]} onChange={(e) => setRates((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))} />
                  <span className="mono">{r.unit}</span>
                </div>
              ))}
            </div>
            <button className="btn btn--gold" type="button" onClick={saveRates}>Save rate card</button>
            {rateSaved && <p className="notice notice--brass"><span className="tick" aria-hidden="true" /><span><strong>Saved.</strong> The card is held in this browser, and it is what quotes and invoices read.</span></p>}
          </div>
          <div className="stack">
            <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>how a vendor gets paid</p>
            <ul className="tl">
              <li><span className="mono mono--ink">STEP 1</span><p className="entry__m">The invoice lands in the agent mailbox.</p></li>
              <li><span className="mono mono--ink">STEP 2</span><p className="entry__m">The desk opens it, reads the amount, and prepares a card.</p></li>
              <li><span className="mono mono--ink">STEP 3</span><p className="entry__m">A person signs off. Nothing is charged without that.</p></li>
              <li><span className="mono mono--ink">STEP 4</span><p className="entry__m">The receipt goes back to the mailbox and the sheet is updated.</p></li>
            </ul>
          </div>
        </div>
      </section>

      <p className="page__foot">A vendor is a subscription, a card and a receipt — three things this desk can prepare and only a person can release.</p>
    </div>
  );
}
